import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  BASE_API,
  DecryptToken,
  enable_stream,
  getLiveStreamUrl,
  CallGETAPI,
} from "../helper/Constants";
import { clearAuthSession } from "../helper/authSession";
import { applyThemePreference, isDarkThemeEnabled } from "../helper/theme";
import { useDispatch, useSelector } from "react-redux";
import { fetchBalance } from "../slices/WalletSlice";
import {
  Bell, LogOut, KeyRound,
  ChevronDown, Menu, Sun, Moon, Phone, AlertTriangle
} from "lucide-react";

const useDarkMode = () => {
  const [dark, setDark] = useState(() => isDarkThemeEnabled());

  useEffect(() => {
    applyThemePreference(dark);
  }, [dark]);

  return [dark, () => setDark((p) => !p)];
};

const formatDateTime = (dateTime) => {
  if (!dateTime) return "";
  const d = new Date(dateTime);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const truncate = (str, max = 10) =>
  str && str.length > max ? `${str.substring(0, max)}…` : str;

const brandLogo = `${process.env.PUBLIC_URL || ""}/voipflow-logo.svg`;

const Dropdown = ({ trigger, children, align = "right" }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <div onClick={() => setOpen((p) => !p)} className="cursor-pointer">
        {trigger}
      </div>
      {open && (
        <div
          className={`absolute ${align === "right" ? "right-0" : "left-0"} top-full mt-2 z-50 
            min-w-[220px] rounded-xl bg-white dark:bg-neutral-950 shadow-xl 
            ring-1 ring-black/5 dark:ring-white/10 overflow-hidden
            animate-in fade-in slide-in-from-top-2 duration-150`}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      )}
    </div>
  );
};

// ─── Live Call Pill ───────────────────────────────────────────────────────────
const CallPill = ({ count, label, color }) => (
  <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${color}`}>
    <Phone size={11} />
    <span>{count} {label}</span>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const Header = ({ navHandler, user_id }) => {
  const dispatch = useDispatch();
  const [dark, toggleDark] = useDarkMode();

  const [userEmail, setUserEmail] = useState(null);
  const [userType, setUserType] = useState(null);
  const [userId, setUserId] = useState(null);
  const [showButton, setShowButton] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifError, setNotifError] = useState(null);
  const [showExpirationWarning, setShowExpirationWarning] = useState(false);
  const [callCounts, setCallCounts] = useState({ campaignCallsCount: 0, extensionCallsCount: 0 });
  const [outboundAllowance, setOutboundAllowance] = useState(null);

  const endDate = useSelector((state) => state.wallet.endDate);
  const homePath = Number(userType) === 3 ? "/calls" : "/dashboard";

  // ── Bootstrap: decode token once
  useEffect(() => {
    try {
      const token = localStorage.getItem("psx_token");
      const decoded = DecryptToken(token);
      if (decoded) {
        setUserEmail(decoded.first_name);
        setUserType(decoded.user_type);
        setUserId(decoded.user_id);
      }
    } catch (e) {
      console.error("Token error:", e);
    }
  }, []);

  // ── Balance
  useEffect(() => { dispatch(fetchBalance()); }, [dispatch]);

  useEffect(() => {
    if (!userId || Number(userType) === 1) return undefined;
    let active = true;
    const loadAllowance = async () => {
      try {
        const response = await CallGETAPI("api/phone/outbound-allowance");
        const data = response?.data?.data;
        if (active && response?.status && data?.outbound_enabled) {
          setOutboundAllowance(data);
        } else if (active) {
          setOutboundAllowance(null);
        }
      } catch (error) {
        if (active) setOutboundAllowance(null);
      }
    };
    loadAllowance();
    const timer = window.setInterval(loadAllowance, 60000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [userId, userType]);

  // ── Expiration warning
  useEffect(() => {
    if (!endDate) return;
    const check = () => {
      const days = Math.ceil((new Date(endDate) - new Date()) / 86400000);
      setShowExpirationWarning(days <= 10 && days > 0);
    };
    check();
    const id = setInterval(check, 86400000);
    return () => clearInterval(id);
  }, [endDate]);

  // ── Responsive show button
  useEffect(() => {
    const handler = () => setShowButton(window.innerWidth > 1200);
    window.addEventListener("resize", handler);
    handler();
    return () => window.removeEventListener("resize", handler);
  }, []);

  // ── Notifications SSE
  useEffect(() => {
    if (!enable_stream) return;
    const es = new EventSource(`${BASE_API}api/notifications/stream`);
    es.onmessage = ({ data }) => {
      try {
        const { type, data: payload } = JSON.parse(data);
        if (type === "init") {
          setNotifications(payload);
          setUnreadCount(payload.filter((n) => !n.isRead).length);
        } else if (type === "create") {
          setNotifications((p) => [payload, ...p]);
          if (!payload.isRead) setUnreadCount((c) => c + 1);
        } else if (type === "delete") {
          setNotifications((p) => {
            const next = p.filter((n) => String(n.id) !== String(payload.id));
            setUnreadCount(next.filter((n) => !n.isRead).length);
            return next;
          });
        }
      } catch (e) {
        console.error("SSE parse error:", e);
        setNotifError("Failed to process notification.");
      }
    };
    es.onerror = () => {
      setNotifError("Connection lost.");
      es.close();
    };
    return () => es.close();
  }, []);

  // ── Live calls SSE
  const handleLiveSSE = useCallback(({ data }) => {
    try {
      const parsed = JSON.parse(data);
      if (parsed.type === "update" && parsed.data?.counts) {
        setCallCounts(parsed.data.counts);
      }
    } catch (e) {
      console.error("Live call SSE error:", e);
    }
  }, []);

  useEffect(() => {
    if (!enable_stream) return;

    const streamUrl = getLiveStreamUrl({
      userId: user_id || userId,
      userType,
    });

    if (!streamUrl) return;

    const es = new EventSource(streamUrl);
    es.onmessage = handleLiveSSE;
    es.onerror = () => es.close();
    return () => es.close();
  }, [handleLiveSSE, userId, userType, user_id]);

  // ── Handlers
  const handleLogout = () => {
    clearAuthSession();
    toast.success("Logged out successfully");
    window.location.replace("/");
  };

  return (
    <header className="admin-glass-header fixed top-0 left-0 right-0 z-50 flex h-[65px] items-center px-4 transition-colors duration-300 md:px-6">

      <div className="flex items-center justify-between w-full gap-4">

        {/* ── Left: Logo + Mobile Menu + Call Pills ── */}
        <div className="flex items-center gap-4">
          {/* Logo */}
          <Link
            to={homePath}
            className="group flex items-center gap-3 text-left no-underline"
          >
            <div className="overflow-hidden rounded-2xl border border-sky-200 bg-white shadow-[0_12px_30px_rgba(8,56,86,0.18)] transition-transform duration-200 group-hover:scale-105 dark:border-sky-900/40 dark:bg-neutral-950 dark:shadow-[0_14px_32px_rgba(8,56,86,0.35)]">
              <img
                src={brandLogo}
                alt="VoIPFlow logo"
                className="h-10 w-10 object-cover"
              />
            </div>
            <span className="hidden text-lg font-extrabold tracking-tight sm:block">
              <span className="text-slate-900 dark:text-white">VoIP</span>
              <span className="ml-1.5 text-sky-700 dark:text-cyan-400">Flow</span>
            </span>
          </Link>

          {/* Mobile menu icon */}
          <button
            onClick={navHandler}
            className="lg:hidden p-1.5 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-neutral-900 transition-colors"
          >
            <Menu size={22} />
          </button>

          {/* Live call pills */}
          <div className="hidden md:flex items-center gap-2">
            {outboundAllowance && (
              <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300" title="Outbound minutes remaining">
                Outbound {outboundAllowance.remaining_minutes}/{outboundAllowance.allocated_minutes} min
              </span>
            )}
            {userType !== 3 && (
              <CallPill
                count={callCounts.campaignCallsCount}
                label="Campaign"
                color="bg-sky-50 text-sky-700 dark:bg-sky-500/10 dark:text-sky-300"
              />
            )}
            {userType !== 4 && (
              <CallPill
                count={callCounts.extensionCallsCount}
                label="Extension"
                color="bg-slate-900 text-white dark:bg-cyan-950/70 dark:text-cyan-100"
              />
            )}
          </div>
        </div>

        {/* ── Right: Nav Items ── */}
        <div className="flex items-center gap-1 md:gap-2">

          {/* Expiration warning */}
          {showExpirationWarning && (
            <div className="flex items-center gap-1.5 rounded-lg bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 dark:bg-amber-500/10 dark:text-amber-300">
              <AlertTriangle size={13} />
              <span className="hidden sm:block">Plan Expiring Soon</span>
            </div>
          )}

          {/* Expire date */}
          {showButton && endDate && (
            <span className="hidden xl:block rounded-lg bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700 dark:bg-amber-500/10 dark:text-amber-200">
              Expires: {formatDateTime(endDate)}
            </span>
          )}

          {/* Dark mode toggle */}
          <button
            onClick={toggleDark}
            className="rounded-lg p-2 text-slate-500 transition-all duration-200 hover:bg-sky-50 hover:text-sky-700 dark:text-slate-400 dark:hover:bg-sky-950/40 dark:hover:text-sky-200"
            aria-label="Toggle dark mode"
          >
            {dark ? <Sun size={17} /> : <Moon size={17} />}
          </button>

          {/* Notifications */}
          <Dropdown
            trigger={
              <div className="relative rounded-lg p-2 text-slate-500 transition-all duration-200 hover:bg-sky-50 hover:text-sky-700 dark:text-slate-400 dark:hover:bg-sky-950/40 dark:hover:text-sky-200">
                <Bell size={17} />
                {unreadCount > 0 && (
                  <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-sky-700 text-[9px] font-bold text-white dark:bg-sky-500 dark:text-white">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </div>
            }
          >
            <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">Notifications</p>
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 mt-0.5">
                {notifications.length > 0
                  ? `${notifications.length} notification${notifications.length !== 1 ? "s" : ""}`
                  : "You're all caught up"}
              </p>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {notifError && (
                <div className="px-4 py-3 text-xs text-red-500">{notifError}</div>
              )}
              {!notifError && notifications.length === 0 && (
                <div className="px-4 py-6 text-center text-sm text-slate-400 dark:text-slate-500">
                  No notifications yet
                </div>
              )}
              {notifications.map((n) => (
                <div
                  key={String(n.id)}
                  className={`px-4 py-3 border-b border-slate-50 dark:border-slate-700/50 last:border-0 
                    hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors ${!n.isRead ? "bg-slate-100 dark:bg-slate-800/80" : ""}`}
                >
                  <div className="flex gap-2.5">
                    <div className="mt-0.5 p-1.5 rounded-full bg-slate-200 dark:bg-slate-700 flex-shrink-0">
                      <Bell size={10} className="text-slate-700 dark:text-slate-200" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-200 leading-snug">{n.message}</p>
                      <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">{formatDateTime(n.createdAt)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Dropdown>

          {/* Profile */}
          <Dropdown
            trigger={
              <div className="flex cursor-pointer items-center gap-1.5 rounded-lg px-2.5 py-1.5 transition-colors hover:bg-sky-50 dark:hover:bg-sky-950/40">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-sky-600 to-cyan-600 text-xs font-bold text-white">
                  {userEmail ? userEmail[0].toUpperCase() : "U"}
                </div>
                {userEmail && (
                  <span className="hidden md:block text-sm font-medium text-slate-700 dark:text-slate-200">
                    {truncate(userEmail)}
                  </span>
                )}
                <ChevronDown size={13} className="text-slate-400 dark:text-slate-500" />
              </div>
            }
          >
            {/* Profile header */}
            <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-sky-600 to-cyan-600 font-bold text-white">
                  {userEmail ? userEmail[0].toUpperCase() : "U"}
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-800 dark:text-white">{userEmail}</p>
                  {/* <p className="text-xs text-slate-400 dark:text-slate-500">Web Designer</p> */}
                </div>
              </div>
            </div>

            <Link
              to="/change-password"
              className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-slate-700 transition-colors hover:bg-sky-50 dark:text-slate-200 dark:hover:bg-sky-500/10"
            >
              <KeyRound size={15} />
              <span>Change password</span>
            </Link>

            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 dark:text-red-400 
                hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
            >
              <LogOut size={15} />
              <span>Sign Out</span>
            </button>
          </Dropdown>
        </div>
      </div>
    </header>
  );
};

export default Header;
